import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, resolveMediaUrl } from '../utils/api';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useSavedProducts } from '../contexts/SavedProductsContext';

export default function Cart() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showAlert } = useNotification();
  const { user } = useAuth();
  const { cart, cartCount, addToCart, updateQuantity, removeFromCart, clearCart } = useCart();
  const { savedForLater, addToSavedForLater, removeFromSavedForLater } = useSavedProducts();

  const [loading, setLoading] = useState(false);
  const [recommended, setRecommended] = useState([]);
  const [promoCode, setPromoCode] = useState('');

  const subtotal = cart.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
  const total = subtotal;

  useEffect(() => {
    let cancelled = false;
    const excludeIds = cart.map((item) => item.id);
    api.getRecommendedProducts({ context: 'cart', limit: 8, excludeIds })
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setRecommended(data.slice(0, 4));
      })
      .catch(() => {
        api.getProducts(null, null)
          .then((fallbackData) => {
            if (!cancelled && Array.isArray(fallbackData)) {
              const filtered = fallbackData.filter((p) => !excludeIds.includes(p.id));
              setRecommended(filtered.slice(0, 4));
            }
          })
          .catch(() => {});
      });
    return () => { cancelled = true; };
  }, [cart]);

  const handleCheckout = async () => {
    if (!user) {
      showAlert('Please login to checkout');
      navigate('/login?redirect=/cart');
      return;
    }

    const branchId = cart.length > 0 ? cart[0].branch_id : 1;

    setLoading(true);
    try {
      const orderData = {
        branch_id: branchId,
        items: cart.map(item => ({ product_id: item.id, quantity: item.quantity || 1 })),
        delivery_address: 'Store Pickup',
        contact_phone: user.phone || '0000000000',
      };

      const order = await api.createOrder(orderData);

      // Initiate Paymob payment
      try {
        const payment = await api.initiatePayment('order', order.id);
        // Redirect to payment iFrame page (don't clear cart yet — clear on success)
        navigate(`/payment?key=${encodeURIComponent(payment.payment_key)}&iframe=${encodeURIComponent(payment.iframe_id)}&type=order&id=${order.id}`);
      } catch (payErr) {
        // If payment gateway is not configured, fall back to direct order
        console.warn('Payment initiation failed, order created without payment:', payErr);
        clearCart();
        showAlert(t('cart.orderPlaced'));
        navigate('/profile');
      }
    } catch (error) {
      console.error('Checkout failed', error);
      showAlert(error?.message || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveForLater = async (item) => {
    try {
      await addToSavedForLater(item.id, item.quantity || 1);
      api.trackProductEvent({ productId: item.id, eventType: 'save_for_later_add', context: 'cart' }).catch(() => {});
      removeFromCart(item.id);
      showAlert('Moved to save for later');
    } catch (error) {
      showAlert(error?.message || 'Could not save item for later');
    }
  };

  const handleMoveToCart = async (savedItem) => {
    try {
      addToCart(savedItem.product, savedItem.quantity || 1);
      api.trackProductEvent({ productId: savedItem.product_id, eventType: 'add_to_cart', context: 'cart' }).catch(() => {});
      await removeFromSavedForLater(savedItem.product_id);
      showAlert('Moved back to cart');
    } catch (error) {
      showAlert(error?.message || 'Could not move item to cart');
    }
  };

  const pageStyle = {
    minHeight: '100vh',
    background: 'var(--bg-base)',
    color: '#fff',
    padding: '7rem 1.5rem 5rem',
    fontFamily: 'var(--font-body)',
  };

  const breadcrumbStyle = {
    marginBottom: '1.5rem',
    fontSize: '0.875rem',
    color: '#fff',
  };

  const linkStyle = {
    color: '#fff',
    textDecoration: 'none',
  };

  const cardStyle = {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-card)',
    padding: '1.5rem',
  };

  const itemCardStyle = {
    ...cardStyle,
    display: 'flex',
    gap: '1.25rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  };

  const imgWrapStyle = {
    width: '120px',
    minWidth: '120px',
    height: '120px',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
    background: 'var(--bg-dark)',
    border: '1px solid var(--border)',
    flexShrink: 0,
  };

  const imgStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  };

  const qtyWrapStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
  };

  const qtyBtnStyle = {
    width: '36px',
    height: '36px',
    border: 'none',
    background: 'var(--input-bg)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const summaryRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
    fontSize: '0.95rem',
  };

  const trustLineStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.75rem',
    fontSize: '0.8rem',
    color: '#fff',
  };

  const checkoutBtnStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    marginTop: '1rem',
    background: 'var(--orange)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-heading)',
    fontSize: '1rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
  };

  const recoCardStyle = {
    ...cardStyle,
    padding: '1rem',
    textAlign: 'center',
    maxWidth: '220px',
  };

  return (
    <div className="cart-page" style={pageStyle}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Breadcrumbs */}
        <nav style={breadcrumbStyle} aria-label="Breadcrumb">
          <Link to="/" style={linkStyle}>{t('nav.home')}</Link>
          <span style={{ margin: '0 0.5rem' }}>›</span>
          <span>{t('cart.title')}</span>
        </nav>

        {cart.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 2rem', ...cardStyle }}>
            <p style={{ marginBottom: '1rem', color: '#fff' }}>{t('cart.empty')}</p>
            <Link to="/products" style={{ ...linkStyle, textDecoration: 'underline' }}>
              {t('cart.browseProducts')}
            </Link>
          </div>
        ) : (
          <>
            <div className="cart-grid">
              {/* Left: Your Shopping Cart */}
              <div>
                <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                  {t('cart.title')}
                </h1>
                <p style={{ color: '#fff', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                  {t('cart.itemsInBag', { count: cartCount })}
                </p>

                <div>
                  {cart.map((item) => (
                    <div key={item.id} style={itemCardStyle}>
                      <div style={imgWrapStyle}>
                        {item.photo_url ? (
                          <img src={resolveMediaUrl(item.photo_url)} alt={item.name} style={imgStyle} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: '2rem', opacity: 0.5 }}>🧴</span>
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>
                          {item.name}
                        </h2>
                        <p style={{ fontSize: '0.85rem', color: '#fff', marginBottom: '0.5rem' }}>
                          {item.category || 'General'} • Size: Universal
                        </p>
                        <p style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700, color: '#fff', marginBottom: '0.75rem' }}>
                          {item.price} EGP
                        </p>
                        <div style={qtyWrapStyle}>
                          <button
                            type="button"
                            aria-label="Decrease quantity"
                            style={qtyBtnStyle}
                            onClick={() => updateQuantity(item.id, -1)}
                          >
                            −
                          </button>
                          <span style={{ minWidth: '2rem', textAlign: 'center', fontSize: '0.95rem', color: '#fff' }}>{item.quantity || 1}</span>
                          <button
                            type="button"
                            aria-label="Increase quantity"
                            style={qtyBtnStyle}
                            onClick={() => updateQuantity(item.id, 1)}
                          >
                            +
                          </button>
                        </div>
                        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.id)}
                            style={{ background: 'none', border: 'none', color: '#fff', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            🗑 {t('cart.remove')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSaveForLater(item)}
                            style={{ background: 'none', border: 'none', color: '#fff', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            ♡ {t('cart.saveForLater')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Order Summary */}
              <div className="cart-order-summary-sticky">
                <div style={cardStyle}>
                  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>
                    {t('cart.orderSummary')}
                  </h2>
                  <div style={summaryRowStyle}>
                    <span>{t('cart.subtotal')}</span>
                    <span>{subtotal.toFixed(2)} EGP</span>
                  </div>
                  <div style={summaryRowStyle}>
                    <span>{t('cart.shipping')}</span>
                    <span style={{ color: '#fff' }}>{t('cart.shippingFree')}</span>
                  </div>
                  <div style={summaryRowStyle}>
                    <span>{t('cart.tax')}</span>
                    <span>—</span>
                  </div>
                  <div style={{ ...summaryRowStyle, marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)', fontSize: '1.1rem', fontWeight: 700 }}>
                    <span>{t('cart.total')}</span>
                    <span style={{ color: '#fff', fontSize: '1.25rem' }}>{total.toFixed(2)} EGP</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleCheckout}
                    disabled={loading}
                    style={checkoutBtnStyle}
                  >
                    {loading ? t('common.loading') : t('cart.proceedToCheckout')} →
                  </button>

                  <div style={{ marginTop: '1.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', color: '#fff', marginBottom: '0.5rem' }}>
                      {t('cart.promoCode')}
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        placeholder={t('cart.enterCode')}
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.5rem 0.75rem',
                          background: 'var(--input-bg)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          color: '#fff',
                          fontSize: '0.9rem',
                        }}
                      />
                      <button
                        type="button"
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'none',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          color: '#fff',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                        }}
                      >
                        {t('cart.apply')}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginTop: '1.25rem' }}>
                    <div style={trustLineStyle}>✓ {t('cart.secureCheckout')}</div>
                    <div style={trustLineStyle}>📦 {t('cart.freeDeliveryOver')}</div>
                    <div style={trustLineStyle}>↻ {t('cart.returnPolicy')}</div>
                  </div>
                </div>
              </div>
            </div>

            {savedForLater.length > 0 && (
              <section style={{ marginTop: '2rem' }}>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.2rem', fontWeight: 700, marginBottom: '1rem' }}>
                  Saved for later ({savedForLater.length})
                </h2>
                {savedForLater.map((savedItem) => (
                  <div key={`${savedItem.list_type}-${savedItem.product_id}`} style={itemCardStyle}>
                    <div style={imgWrapStyle}>
                      {savedItem.product?.photo_url ? (
                        <img src={resolveMediaUrl(savedItem.product.photo_url)} alt={savedItem.product.name} style={imgStyle} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '2rem', opacity: 0.5 }}>🧴</span>
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <h3 style={{ marginBottom: '0.35rem' }}>{savedItem.product?.name}</h3>
                      <p style={{ marginBottom: '0.5rem', color: '#fff' }}>
                        {savedItem.product?.price} EGP • Qty: {savedItem.quantity}
                      </p>
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => handleMoveToCart(savedItem)}
                          style={{ background: 'none', border: 'none', color: '#fff', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Move to cart
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFromSavedForLater(savedItem.product_id)}
                          style={{ background: 'none', border: 'none', color: '#fff', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </section>
            )}

            {/* You might also like */}
            {recommended.length > 0 && (
              <section style={{ marginTop: '3rem', maxWidth: '1200px', marginLeft: 'auto', marginRight: 'auto' }}>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
                  {t('cart.youMightAlsoLike')}
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1.25rem' }}>
                  {recommended.slice(0, 4).map((product) => (
                    <div key={product.id} style={recoCardStyle}>
                      {product.photo_url ? (
                        <img
                          src={resolveMediaUrl(product.photo_url)}
                          alt={product.name}
                          style={{ width: '100%', height: '120px', objectFit: 'contain', background: 'var(--bg-dark)', borderRadius: 'var(--radius-sm)', marginBottom: '0.75rem' }}
                        />
                      ) : (
                        <div style={{ width: '100%', height: '120px', background: 'var(--bg-dark)', borderRadius: 'var(--radius-sm)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '1.5rem', opacity: 0.5 }}>🧴</span>
                        </div>
                      )}
                      <p style={{ fontFamily: 'var(--font-heading)', fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.35rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {product.name}
                      </p>
                      <p style={{ color: '#fff', fontFamily: 'monospace', fontWeight: 700, marginBottom: '0.75rem' }}>
                        {product.price} EGP
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          api.trackProductEvent({ productId: product.id, eventType: 'click', context: 'cart' }).catch(() => {});
                          addToCart(product);
                          api.trackProductEvent({ productId: product.id, eventType: 'add_to_cart', context: 'cart' }).catch(() => {});
                          showAlert(t('cart.addedToCart'));
                        }}
                        style={{
                          width: '100%',
                          padding: '0.4rem 0.75rem',
                          background: 'transparent',
                          border: '1px solid var(--orange)',
                          borderRadius: 'var(--radius-sm)',
                          color: '#fff',
                          fontFamily: 'var(--font-heading)',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                        }}
                      >
                        {t('products.addToCart')}
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
      <style>{`
        .cart-page input::placeholder {
          color: #fff;
          opacity: 0.75;
        }
      `}</style>
    </div>
  );
}
